import { cx } from "@suse-o11y/class-variance";
import { fromJSON } from "@suse-o11y/from-json";
import { IconCaretDown } from "@suse-o11y/icons";
import { identity } from "@suse-o11y/identity";
import { isString } from "@suse-o11y/is-string";
import { mergeRefs } from "@suse-o11y/merge-refs";
import type { SelectPaths } from "@suse-o11y/select-paths";
import { useOnInvalidFormFocus } from "@suse-o11y/signal/form";
import { sortBy } from "@suse-o11y/sort-by";
import { toJSON } from "@suse-o11y/to-json";
import { useControlledState } from "@suse-o11y/use-controlled-state";
import AntdSelect, { type SelectProps as AntdSelectProps, type RefSelectProps } from "antd/es/select";
import React from "react";

import { useFormFieldContext } from "~/components/form/use-form-field-context";
import { Truncated } from "~/components/truncated";

import { fuzzSearchInGroups, fuzzSearchInOptions } from "./fuzzSearch";
import styles from "./select.module.css";
import { type SelectOption, type SelectOptionGroup, isSelectOption } from "./types";

const stringifyOptions = <TValue,>(
	groupsOrOptions: ReadonlyArray<SelectOptionGroup<TValue>> | ReadonlyArray<SelectOption<TValue>>,
) =>
	groupsOrOptions.map((groupOrOption) => {
		if (isSelectOption(groupOrOption)) {
			return { ...groupOrOption, value: toJSON(groupOrOption.value) };
		}

		return {
			...groupOrOption,
			options: groupOrOption.options.map((option) => ({ ...option, value: toJSON(option.value) })),
		};
	});

export interface SelectProps<TValue>
	extends Pick<
		AntdSelectProps<TValue>,
		| "placeholder"
		| "disabled"
		| "notFoundContent"
		| "onDropdownVisibleChange"
		| "virtual"
		| "autoFocus"
		| "loading"
		| "showArrow"
	> {
	value?: undefined | TValue;
	onChange?: React.Dispatch<TValue>;
	search?: string;
	onSearch?: React.Dispatch<string>;
	/**
	 * @default []
	 */
	filterBy?: ReadonlyArray<SelectPaths<SelectOption<TValue>, string | number>>;
	options: ReadonlyArray<SelectOptionGroup<TValue>> | ReadonlyArray<SelectOption<TValue>>;
	/**
	 * @default false
	 */
	hasMoreItems?: boolean;
	/**
	 * Determines how many items can be shown at once
	 */
	maxItems?: number;
	allowArbitraryValues?: boolean;
	className?: string;
}

export const Select = React.forwardRef(
	<TValue,>(
		{
			value,
			onChange,
			search,
			onSearch,
			options,
			filterBy = [],
			hasMoreItems = false,
			allowArbitraryValues = false,
			maxItems = undefined,
			className,
			...props
		}: SelectProps<TValue>,
		ref: React.Ref<RefSelectProps>,
	): React.ReactElement => {
		const formField = useFormFieldContext();
		const focusRef = useOnInvalidFormFocus<RefSelectProps>(formField?.state);

		const [controlledValue, setControlledValue] = useControlledState(value, onChange);
		const [controlledSearch, setControlledSearch] = useControlledState(search ?? "", onSearch);
		const query = controlledSearch.trim().toLocaleLowerCase();
		// make the filterBy array a string so that we can use it in useMemo
		const filterByStringified = sortBy(filterBy, identity).join("||");

		const filteredOptions = React.useMemo(() => {
			if (options.length === 0 || query.length === 0 || filterByStringified.length === 0) {
				return stringifyOptions(options);
			}

			const paths = filterByStringified.split("||");

			if (isSelectOption(options[0])) {
				return stringifyOptions(fuzzSearchInOptions(query, paths, options as Array<SelectOption<TValue>>));
			}

			return stringifyOptions(fuzzSearchInGroups(query, paths, options as Array<SelectOptionGroup<TValue>>));
		}, [options, filterByStringified, query]);

		const slicedOptions = React.useMemo(
			() => (maxItems == null ? filteredOptions : filteredOptions.slice(0, maxItems)),
			[filteredOptions, maxItems],
		);

		const serializedValue = React.useMemo(() => {
			if (controlledValue == null) {
				return undefined;
			}

			return toJSON(controlledValue);
		}, [controlledValue]);

		const searchValueExistsInOptions = React.useMemo(
			() =>
				slicedOptions.some((option) => {
					if (isSelectOption(option)) {
						return option.value === toJSON(controlledSearch);
					}

					return false;
				}),
			[slicedOptions, controlledSearch],
		);

		const valueExistsInOption = React.useMemo(
			() =>
				slicedOptions.some((option) => {
					if (isSelectOption(option)) {
						return option.value === serializedValue;
					}

					return false;
				}),
			[slicedOptions, serializedValue],
		);

		return (
			<AntdSelect
				dropdownClassName={styles.dropdown}
				dropdownRender={(menu) => <div className={styles.content}>{menu}</div>}
				filterOption={false} // allows applying custom filtering
				onBlur={() => {
					formField?.state.setTouched(true);
				}}
				onChange={(next) => {
					const [error, parsed] = fromJSON<TValue>(next);

					if (error == null) {
						setControlledValue(parsed.unsafe);
					}
				}}
				onSearch={(next) => setControlledSearch(next)}
				optionLabelProp="label"
				ref={mergeRefs([ref, focusRef])}
				searchValue={controlledSearch}
				showSearch
				suffixIcon={<IconCaretDown className="text-grey-500" height={6} width={9} />}
				value={serializedValue}
				{...props}
				className={cx(styles.select, className)}
			>
				{/*
          Show the user's search input as the first option if it doesn't match any available option
          to match the "tags" mode version of the Select
        */}
				{!searchValueExistsInOptions &&
					controlledSearch !== controlledValue &&
					allowArbitraryValues &&
					controlledSearch.trim().length > 0 && (
						<AntdSelect.Option key={`__arbitrary-value__${controlledSearch}`} value={toJSON(controlledSearch)}>
							<Truncated>{controlledSearch}</Truncated>
						</AntdSelect.Option>
					)}

				{slicedOptions.length > 0 && hasMoreItems && (
					<AntdSelect.OptGroup
						label={
							<div className="h-auto whitespace-normal text-13 text-grey-800">
								<span className="font-semibold">Type a few more characters</span>
								<span> to get the full list of results (currently showing {slicedOptions.length} items)</span>
							</div>
						}
					/>
				)}

				{slicedOptions.map((groupOrOption) => {
					if (isSelectOption(groupOrOption)) {
						return (
							<AntdSelect.Option key={groupOrOption.value} label={groupOrOption.label} value={groupOrOption.value}>
								<Truncated>{groupOrOption.label}</Truncated>
							</AntdSelect.Option>
						);
					}

					return (
						<AntdSelect.OptGroup key={groupOrOption.label} label={<Truncated>{groupOrOption.label}</Truncated>}>
							{groupOrOption.options.map((option) => (
								<AntdSelect.Option key={option.value} label={option.label} value={option.value}>
									<Truncated>{option.label}</Truncated>
								</AntdSelect.Option>
							))}
						</AntdSelect.OptGroup>
					);
				})}

				{/*
          If the current selected option doesn't match any option, then it is an arbitrary value.
          Show a final option with the value of the selected option to match the "tags" mode version
          of the Select
        */}
				{!valueExistsInOption && allowArbitraryValues && controlledValue && isString(controlledValue) && (
					<AntdSelect.Option
						label={controlledValue}
						/* Strip the wrapping quotes from the serializedValue to create a pretty label*/
						value={toJSON(controlledValue)}
					>
						<Truncated>{controlledValue}</Truncated>
					</AntdSelect.Option>
				)}
			</AntdSelect>
		);
	},
) as <TValue>(props: SelectProps<TValue> & React.RefAttributes<RefSelectProps>) => React.ReactNode;
